import { makeObservable, observable } from 'mobx';

import type { Aggregate } from 'edit/Aggregate';
import type { SchedulingWindowReference } from 'edit/reference/EditSchedulingWindowReference';
import { formatSchedulingWindowPath, parseSchedulingWindowPath } from 'edit/reference/schedulingWindowPath';
// The wire calls these `references` (the table is scheduling_window_references),
// which collides with the reference-picker's own `SchedulingWindowReference`.
// Aliased rather than renamed: the api layer mirrors the wire.
import type { ReferenceType, SchedulingWindowReference as WireLink } from 'api/scheduling_windows/endpoints/types';

import type { EditableSchedulingWindow } from './EditableSchedulingWindow';

let nextLinkKey = 0;

const DEFAULT_LINK_TYPE: ReferenceType = 'open';

/**
 * A link from this window to another one, which either opens or closes this
 * window alongside itself.
 *
 * A cycle is rejected by the DB trigger `trg_scheduling_window_references_no_cycle`
 * and by the route. The previous form pre-filtered cyclic windows out of its
 * dropdowns, walking a dependency graph it built from the whole window list; that
 * is gone, for the same reason the field validation is — one rule, one place. The
 * server answers and the dialog surfaces it, as it does for folders.
 */
export class EditableWindowLink {
  private readonly _aggregate: Aggregate<EditableSchedulingWindow, EditableWindowLink>;
  /** Stable identity for React list keys; never changes after construction. */
  readonly key = `window-link-${(nextLinkKey += 1)}`;
  linkType: ReferenceType = DEFAULT_LINK_TYPE;
  window: SchedulingWindowReference | null = null;

  constructor(aggregate: Aggregate<EditableSchedulingWindow, EditableWindowLink>, wire?: WireLink) {
    this._aggregate = aggregate;
    makeObservable(this, {
      linkType: observable,
      window: observable.ref,
    });
    if (wire !== undefined) {
      this.linkType = wire.type;
      this.window = parseSchedulingWindowPath(wire.scheduling_window_path);
    }
  }

  /** Back-reference to the owning window, via the shared aggregate. */
  get schedulingWindow(): EditableSchedulingWindow {
    return this._aggregate.parent;
  }

  /** Remove this link from its window. */
  delete = (): void => {
    this._aggregate.remove(this);
  };

  toSave(): WireLink {
    return {
      scheduling_window_path: formatSchedulingWindowPath(this.window),
      type: this.linkType,
    };
  }
}
